/* eslint-disable quote-props */
import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit, ViewChild
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';

import { DateTime, DateTimeUnit } from 'luxon';
import {
  from,
  Observable,
  Subject,
  Subscription,
  take
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Logger } from '@sondermind/datadog-logging';
import { CHAT_STORE, IChatStore } from '@sondermind/data-access/chat';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import {
  EmbeddedClientCreatorDialogComponent,
  BulkClientUploadDialogComponent,
  InviteEntryPoint,
  UploadResult
} from '@sondermind/features/embedded-client';
import { IrisButtonTypes } from '@sondermind/iris/button';
import { IconColors, IconSizes, IconUsageTypes } from '@sondermind/iris/icon';
import { SegmentService } from '@sondermind/segment';

import {
  CLIENT_TYPES,
  ClientStatus,
  CombinedClientAndClientReferralMember
} from '@sondermind/utilities/models-clinical';
import { Patient } from '@sondermind/utilities/models-patient';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { BreakpointsService, FeatureFlags } from '@sondermind/utilities/services';

import { DropdownOption } from '@sondermind/iris/dropdown';
import { CurrentClientStore, CurrentClientTableState, FILTER_ALL } from '@sondermind/data-access/clinical';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';

const CURRENT_CLIENTS_STATUSES =
`${ClientStatus.NOT_INVITED}, ${ClientStatus.INVITED}, ${ClientStatus.CLIENT}`;
const CURRENT_CLIENTS_STATUSES_INCLUDING_INACTIVE =
`${ClientStatus.NOT_INVITED}, ${ClientStatus.INVITED}, ${ClientStatus.CLIENT}, ${ClientStatus.INACTIVE}`;

@Component({
  selector: 'member-page-current-clients-list',
  templateUrl: './current-clients-list.component.html',
  styleUrls: ['./current-clients-list.component.scss'],
  standalone: false
})

export class CurrentClientsListPage implements OnInit, OnDestroy, AfterViewInit {
  readonly iconSizes = IconSizes;
  IrisButtonTypes = IrisButtonTypes;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  user: ProviderUser;
  clients: CombinedClientAndClientReferralMember[] = [];
  userSub: Subscription;
  routeSub: Subscription;
  clientDataSub: Subscription;
  // Sorting attributes
  loading: boolean;
  loadingData = 'Loading please wait ....';
  globalFilterVal: string;
  paginationData: any = {
    total: 0, filtered: 0, pages: 0, number: 1, size: 15
  };
  resultsLength = 0;
  dataSource = new MatTableDataSource();
  displayedColumns: string[] = [
    'last_name',
    'next_session_datetime',
    'days_since_last_session',
    'actions'
  ];
  timezone: string = '';
  status: string;
  emptyClientStatusFilter: boolean = false;
  sortedByMenu: boolean = false;

  includeInactiveClients: boolean = true;

  currentClientType = CLIENT_TYPES.existing;
  ClientStatus = ClientStatus;

  tableStateSub: Subscription;

  hideAvailability = false;
  tableState: CurrentClientTableState = {
    page: 1,
    pageIndex: 0,
    pageSize: 15,
    sortBy: 'last_name',
    sortAscending: true,
    sortDirection: 'asc'
  };

  filterSessionTimes = {
    'day': "Today's clients",
    'week': "This week's clients",
    'month': "This month's clients",
    [FILTER_ALL]: 'All time clients'
  };

  buttonType: IrisButtonTypes = IrisButtonTypes.PRIMARY;
  addButtonText: string = 'Add client';

  @ViewChild('newPatientModal') newPatientModal: EmbeddedClientCreatorDialogComponent;
  @ViewChild('bulkUploadModal') bulkUploadModal!: BulkClientUploadDialogComponent;

  showBulkUploadButton: boolean = false;

  filterOptions = {
    filter: [
      { key: 'client_type', value: this.currentClientType },
      { key: 'status', value: CURRENT_CLIENTS_STATUSES }
    ],
    page: {
      size: this.tableState.pageSize,
      number: 1
    },
    sort: {
      key: this.tableState.sortBy,
      asc: this.tableState.sortAscending
    }
  };
  IconUsageTypes = IconUsageTypes;
  IconSizes = IconSizes;
  IconColors = IconColors;
  sortForm: FormGroup;
  sortOptions: Array<DropdownOption<string>> = [
    { name: 'Default (name, A-Z)', value: 'nameAsc' },
    { name: 'Name, Z-A', value: 'nameDesc' },
    { name: 'Upcoming session, soonest to latest', value: 'soonestToLatest' },
    { name: 'Upcoming session, latest to soonest', value: 'latestToSoonest' },
    { name: 'Newest clients to oldest', value: 'newestToOldest' },
    { name: 'Oldest to newest', value: 'oldestToNewest' },
    { name: 'Days since last session, few to many', value: 'fewToMany' },
    { name: 'Days since last session, many to few', value: 'manyToFew' }
  ];

  sessionOptions: Array<DropdownOption<string>> = [
    { name: 'Today\'s clients', value: 'day' },
    { name: 'This week\'s clients', value: 'week' },
    { name: 'This month\'s clients', value: 'month' },
    { name: 'All time clients', value: 'all' }
  ];

  clientStatusOptions: Array<DropdownOption<string>> = [
    { name: 'Active clients', value: 'active' },
    { name: 'Inactive clients', value: 'inactive' },
    { name: 'Both active and inactive', value: 'all' }
  ];

  constructor(
    private appService: SondermindApplicationService,
    private route: ActivatedRoute,
    private router: Router,
    public currentClientStore: CurrentClientStore,
    public breakPoint: BreakpointsService,
    private snackBar: MatSnackBar,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
    private featureFlags: FeatureFlags,
    @Inject(CHAT_STORE)
    private chatStore: IChatStore,
    private formBuilder: FormBuilder,
    private segmentService: SegmentService<SondermindEvents>,
    private logger: Logger
  ) { }

  private destroyedSubj: Subject<void> = new Subject<void>();
  destroyed$: Observable<void> = this.destroyedSubj.asObservable();

  ngOnInit(): void {
    this.sortForm = this.formBuilder.group({
      sortFormControl: ['nameAsc', Validators.required],
      sessionFormControl: ['all', Validators.required],
      clientStatusFormControl: ['active', Validators.required]
    });

    this.sortForm.get('sortFormControl')?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.sortClients(value);
    });

    this.sortForm.get('sessionFormControl')?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.filterSessionTime(value);
    });

    this.sortForm.get('clientStatusFormControl')?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.filterActiveClients(value);
    });

    this.setupSubscriptions();
    this.includeInactiveClients = this.currentClientStore.includeInactiveClients;

    this.setFilterOptions();
    this.setSortFromStore();

    if (!this.currentClientStore.filters) {
      this.currentClientStore.filters = this.filterOptions;
    }
    this.currentClientStore.loadClients();
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngAfterViewInit(): void {
    this.tableState.pageSize = this.paginator?.pageSize;

    this.sort.sortChange.subscribe((sorting: Sort) => {
      if (sorting.active === 'last_name') {
        this.currentClientStore.selectedSort = sorting.direction === 'asc' ? 'nameAsc' : 'nameDesc';
      }

      if (sorting.active === 'next_session_datetime') {
        this.currentClientStore.selectedSort = sorting.direction === 'asc' ? 'soonestToLatest' : 'latestToSoonest';
      }

      if (sorting.active === 'days_since_last_session') {
        this.currentClientStore.selectedSort = sorting.direction === 'asc' ? 'fewToMany' : 'manyToFew';
      }

      // hack the default mat-sort behavior to align with requirements
      if (this.tableState.sortBy !== sorting.active && !this.sortedByMenu) {
        this.tableState.sortDirection = 'asc';
        this.tableState.sortAscending = true;
      } else if (this.tableState.sortDirection === sorting.direction) {
        this.tableState.sortDirection = this.tableState.sortDirection === 'asc' ? 'desc' : 'asc';
        this.tableState.sortAscending = this.tableState.sortDirection === 'asc';
      } else {
        this.tableState.sortDirection = sorting.direction;
        this.tableState.sortAscending = this.tableState.sortDirection === 'asc';
      }

      this.tableState.sortBy = sorting.active;
      this.sortedByMenu = false;
      this.currentClientStore.updateTableState(this.tableState);
      this.resetPaginationAndReload();
    });
  }

  ngOnDestroy(): void {
    this.destroyedSubj.next();
    this.destroyedSubj.complete();
  }

  setupSubscriptions(): void {
    this.userSub = this.appService.currentUser$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((u) => {
      this.user = u;
      this.timezone = u.timeZone;
    });

    this.routeSub = this.route.data.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((data) => {
      if (data.statuses) {
        this.reloadData();
      }
    });

    this.featureFlags.check$('hideAvailabilityAndMatches').pipe(
      takeUntil(this.destroyed$)
    ).subscribe((res) => {
      this.hideAvailability = res;
    });

    this.clientDataSub = this.currentClientStore.clientData$
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((response) => {
        if (response?.data) {
          this.paginationData = { ...response.meta.counts, ...response.meta.page };
          this.clients = response.data;
          if (this.sort) {
            this.sort.direction = this.tableState.sortAscending ? 'asc' : 'desc';
          }
          this.loading = false;
          this.loadingData = '';
          this.emptyClientStatusFilter = false;
          this.currentClientStore.initialLoadComplete = true;
        }
      });

    this.tableStateSub = this.currentClientStore.tableState$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((res) => {
      this.tableState = res;
    });

    this.featureFlagService.isEnabled$(FeatureFlagKey.ALLOW_BULK_UPLOAD_CLIENTS).pipe(
      takeUntil(this.destroyed$)
    ).subscribe((enabled) => {
      this.showBulkUploadButton = enabled || false;
    });
  }

  sortClients(value): void {
    this.sortedByMenu = true;
    this.currentClientStore.selectedSort = value;
    // eslint-disable-next-line default-case
    switch (value) {
      case 'nameAsc':
        this.sort.sortChange.emit({ active: 'last_name', direction: 'asc' });
        break;
      case 'nameDesc':
        this.sort.sortChange.emit({ active: 'last_name', direction: 'desc' });
        break;
      case 'soonestToLatest':
        this.sort.sortChange.emit({ active: 'next_session_datetime', direction: 'asc' });
        break;
      case 'latestToSoonest':
        this.sort.sortChange.emit({ active: 'next_session_datetime', direction: 'desc' });
        break;
      case 'newestToOldest':
        this.sort.sortChange.emit({ active: 'created_at', direction: 'desc' });
        break;
      case 'oldestToNewest':
        this.sort.sortChange.emit({ active: 'created_at', direction: 'asc' });
        break;
      case 'fewToMany':
        this.sort.sortChange.emit({ active: 'days_since_last_session', direction: 'asc' });
        break;
      case 'manyToFew':
        this.sort.sortChange.emit({ active: 'days_since_last_session', direction: 'desc' });
        break;
    }
  }

  handleNewPatientClick(): void {
    this.trackInviteInitiated('single');
    this.newPatientModal.open(new Patient()).subscribe((obj) => {
      if (obj) { this.patientAdded(obj) }
    });
  }

  handleBulkUploadClick(): void {
    this.trackInviteInitiated('bulk');
    this.bulkUploadModal.open().subscribe((results: UploadResult[] | null) => {
      if (results && results.length > 0) {
        this.bulkUploadCompleted(results);
      }
    });
  }

  patientAdded(patient: Patient): void {
    this.snackBar.open('New client created successfully!', 'Dismiss');
    this.router.navigate(['clients', patient.id, 'client_notes']);
  }

  bulkUploadCompleted(results: UploadResult[]): void {
    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.filter((r) => !r.success).length;

    if (failureCount === 0) {
      this.snackBar.open(`${successCount} client${successCount === 1 ? '' : 's'} uploaded successfully!`, 'Dismiss', {
        duration: 5000
      });
    } else {
      this.snackBar.open(`Upload complete: ${successCount} succeeded, ${failureCount} failed`, 'Dismiss', {
        duration: 5000
      });
    }

    // Reload clients list to show newly added clients
    this.currentClientStore.loadClients();
  }

  private trackInviteInitiated(inviteType: 'single' | 'bulk'): void {
    const personaProviderId = this.user?.personaProviderId;
    if (!personaProviderId) { return }

    from(this.segmentService.track<SondermindEvent.ProviderInvitedClientsInitiated>(
      SondermindEvent.ProviderInvitedClientsInitiated,
      {
        personaProviderId,
        inviteType,
        entryPoint: InviteEntryPoint.CURRENT_CLIENTS_LIST
      }
    )).subscribe({
      error: (err) => this.logger.warn('Segment tracking failed for Provider Invited Clients Initiated', { err })
    });
  }

  onPaginate(event): void {
    this.tableState.page = event.pageIndex + 1;
    this.tableState.pageIndex = event.pageIndex;
    this.tableState.pageSize = event.pageSize;
    this.clients = [];
    this.currentClientStore.updateTableState(this.tableState);
    this.reloadData();
  }

  reloadData(): void {
    this.loadingData = 'Loading please wait ....';
    this.loading = true;

    this.filterOptions = {
      filter: [
        { key: 'client_type', value: this.currentClientType }
      ],
      page: {
        size: this.tableState.pageSize,
        number: this.tableState.page
      },
      sort: {
        key: this.tableState.sortBy,
        asc: this.tableState.sortAscending
      }
    };

    this.setFilterOptions();

    this.currentClientStore.filters = this.filterOptions;
    this.currentClientStore.loadClients();
  }

  displayDaysSinceLastSession(days: number): string {
    if (days === 0) {
      return '< 1';
    }
    if (days > 31) {
      return 'Last seen 1+ month ago';
    }
    return days?.toString();
  }

  viewClient(client: CombinedClientAndClientReferralMember): void {
    this.router.navigate(['clients', client.id, 'smart_notes']);
  }

  showChatLink(client: CombinedClientAndClientReferralMember): boolean {
    return !!client.patient?.chatProfileUuid;
  }

  openChat(client: CombinedClientAndClientReferralMember): void {
    this.chatStore.openChatRoomByPlatformIdentity$(client.clientPlatformIdentity).pipe(take(1)).subscribe();
  }

  filterActiveClients(value: string): void {
    this.currentClientStore.activeFilter = value;
    this.currentClientStore.setFilterStatus();
    this.resetPaginationAndReload();
  }

  filterSessionTime(value: string): void {
    this.currentClientStore.sessionFilter = value;
    this.currentClientStore.setFilterStatus();
    this.resetPaginationAndReload();
  }

  // compareFn for KeyValuePipe
  // don't want to sort filterSessionTimes
  unsortedSessionTime(): number {
    return 0;
  }

  clearFilters(): void {
    this.sortForm.patchValue({
      sortFormControl: 'nameAsc',
      sessionFormControl: 'all',
      clientStatusFormControl: 'active'
    });

    this.currentClientStore.resetFilters();
    this.resetPaginationAndReload();
  }

  private setFilterOptions(): void {
    this.setStatusFilter();
    this.setSessionBetweenFilter();
  }

  private setStatusFilter(): void {
    const filter = this.currentClientStore.activeFilter;
    let value;

    if (filter === FILTER_ALL) {
      value = CURRENT_CLIENTS_STATUSES_INCLUDING_INACTIVE;
    } else if (filter === 'active') {
      value = CURRENT_CLIENTS_STATUSES;
    } else {
      value = ClientStatus.INACTIVE;
    }

    this.filterOptions.filter.push(
      {
        key: 'status',
        value
      }
    );
  }

  private setSessionBetweenFilter(): void {
    const filter = this.currentClientStore.sessionFilter;
    const filterKeys = Object.keys(this.filterSessionTimes);

    if (!filterKeys.includes(filter) || filter === FILTER_ALL) { return }
    // eslint-disable-next-line new-cap
    const currentTime = DateTime.now();
    const startTime = currentTime.startOf(filter as DateTimeUnit).toUTC();
    const endTime = currentTime.endOf(filter as DateTimeUnit).toUTC();

    this.filterOptions.filter.push(
      {
        key: 'session_between',
        value: `${startTime},${endTime}`
      }
    );
  }

  private setSortFromStore(): void {
    // eslint-disable-next-line default-case
    switch (this.currentClientStore.selectedSort) {
      case 'nameAsc':
        this.tableState.sortBy = 'last_name';
        this.tableState.sortAscending = true;
        break;
      case 'nameDesc':
        this.tableState.sortBy = 'last_name';
        this.tableState.sortAscending = false;
        break;
      case 'soonestToLatest':
        this.tableState.sortBy = 'next_session_datetime';
        this.tableState.sortAscending = true;
        break;
      case 'latestToSoonest':
        this.tableState.sortBy = 'next_session_datetime';
        this.tableState.sortAscending = false;
        break;
      case 'newestToOldest':
        this.tableState.sortBy = 'created_at';
        this.tableState.sortAscending = false;
        break;
      case 'oldestToNewest':
        this.tableState.sortBy = 'created_at';
        this.tableState.sortAscending = true;
        break;
      case 'fewToMany':
        this.tableState.sortBy = 'days_since_last_session';
        this.tableState.sortAscending = true;
        break;
      case 'manyToFew':
        this.tableState.sortBy = 'days_since_last_session';
        this.tableState.sortAscending = false;
        break;
    }
    this.currentClientStore.updateTableState(this.tableState);
  }

  private resetPaginationAndReload(): void {
    this.tableState.page = 1;
    this.paginator.pageIndex = 0;
    this.reloadData();
  }
}
